import { Features } from '@alsid/common'
import { createEntity } from '@app/entities'
import EntityApplicationSettings from '@app/entities/EntityApplicationSettings'
import type { StoreRoot } from '@app/stores'
import StoreFlags from '@app/stores/helpers/StoreFlags'
import StoreForm from '@app/stores/helpers/StoreForm'
import type {
  IFieldValidator,
  IFieldValidatorDef
} from '@app/stores/helpers/StoreForm/types'
import { InputType } from '@app/stores/helpers/StoreForm/types'
import {
  emailFormat,
  mandatory,
  minValue,
  portFormat
} from '@app/stores/helpers/StoreForm/validators'
import StoreBase from '@app/stores/StoreBase'
import type { IStoreOptions } from '@app/stores/types'
import { ForbiddenAccessError } from '@libs/errors'
import { handleStoreError } from '@libs/errors/handleStoreError'
import { filterFalsies } from '@libs/filterFalsies'
import { checkRbac } from '@libs/rbac/functions'
import type { MutationUpdateApplicationSettings } from '@server/graphql/mutations/applicationSetting'
import { mutationUpdateApplicationSettings } from '@server/graphql/mutations/applicationSetting'
import type { QueryApplicationSettings } from '@server/graphql/queries/management'
import { queryApplicationSettings } from '@server/graphql/queries/management'
import {
  type ApplicationSettings,
  type InputUpdateApplicationSettings,
  type InputUpdateLockoutPolicy,
  SmtpAuthenticationMethod
} from '@server/graphql/typeDefs/types'
import type Maybe from 'graphql/tsutils/Maybe'
import { action, computed, makeObservable, observable } from 'mobx'
import { match } from 'ts-pattern'
import {
  ActivityLogsConfigurationFormFieldName,
  HealthCheckFormFieldName,
  SMTPServerBasicFormFieldName,
  SMTPServerFormFieldName,
  SMTPServerOffice365FormFieldName,
  SecurityConfigurationFormFieldName,
  TelemetryFormFieldName,
  TenableAccountFormFieldName
} from './types'

export const DEFAULT_SMTP_PORT = 25

/**
 * Custom passwords/secrets validator.
 */
export const mandatorySecret = (
  storeApplicationSettings: StoreApplicationSettings,
  form: 'basic' | 'oauth2'
): IFieldValidator => {
  return {
    name: 'mandatory',
    handler: translate => {
      const validator: IFieldValidatorDef = {
        isValid: () => {
          const {
            applicationSettings,
            storeFormSMTPBasicConfiguration,
            storeFormSMTPOffice365Configuration
          } = storeApplicationSettings

          return match(form)
            .with('basic', () => {
              // Is defined is defined (as boolean in the store) or defined after being filled in the form
              const isDefined =
                applicationSettings?.smtpIsAccountPasswordDefined ||
                storeFormSMTPBasicConfiguration.getFieldValueAsString(
                  SMTPServerBasicFormFieldName.smtpAccountPassword
                ) !== ''

              return isDefined
            })
            .with('oauth2', () => {
              // Is defined is defined (as boolean in the store) or defined after being filled in the form
              const isDefined =
                applicationSettings?.smtpIsOffice365SecretDefined ||
                storeFormSMTPOffice365Configuration.getFieldValueAsString(
                  SMTPServerOffice365FormFieldName.smtpOffice365Secret
                ) !== ''

              return isDefined
            })
            .exhaustive()
        },
        onSuccess: () => null,
        onError: () => translate('Enter a value')
      }

      return validator
    }
  }
}

export default class StoreApplicationSettings extends StoreBase {
  public storeFlagsAppSettingsFetch = new StoreFlags(this.storeRoot)
  public storeFlagsAppSettingsSubmit = new StoreFlags(this.storeRoot)

  public translate = this.storeRoot.appTranslator.bindOptions({
    namespaces: [
      'Buttons',
      'Management.System.Configuration.SMTP',
      'Management.System.Configuration.Logs',
      'Management.System.Configuration.Security',
      'Management.System.Configuration.ApplicationSettings',
      'Management.System.Configuration.DataCollection',
      'Management.System.Directories'
    ]
  })

  public storeFormSMTPConfiguration = new StoreForm<SMTPServerFormFieldName>(
    this.storeRoot,
    {
      setup: {
        fields: {
          [SMTPServerFormFieldName.enabled]: {
            label: 'Enabled',
            inputType: InputType.checkbox
          },
          [SMTPServerFormFieldName.smtpAuthenticationMethod]: {
            label: 'Authentication method',
            inputType: InputType.select
          }
        }
      }
    }
  )

  public storeFormSMTPBasicConfiguration =
    new StoreForm<SMTPServerBasicFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [SMTPServerBasicFormFieldName.smtpRelayId]: {
            label: 'Relay',
            description: 'Relay to use to connect to the SMTP server',
            inputType: InputType.select,
            validators: this.storeRoot.environment.config.app.securerelay
              .customerside
              ? [mandatory()]
              : []
          },
          [SMTPServerBasicFormFieldName.smtpServerAddress]: {
            label: 'SMTP server address',
            validators: [mandatory()]
          },
          [SMTPServerBasicFormFieldName.smtpServerPort]: {
            label: 'SMTP server port',
            validators: [portFormat()]
          },
          [SMTPServerBasicFormFieldName.smtpAccount]: {
            label: 'SMTP account'
          },
          [SMTPServerBasicFormFieldName.smtpAccountPassword]: {
            label: 'SMTP account password',
            // Custom component used in the form
            inputType: undefined
          },
          [SMTPServerBasicFormFieldName.smtpEncryption]: {
            label: 'SMTP encryption',
            validators: [mandatory()],
            inputType: InputType.select,
            description:
              'Encryption policy used for the connection to the SMTP server'
          },
          [SMTPServerBasicFormFieldName.emailSender]: {
            label: 'Email address of the sender',
            validators: [mandatory(), emailFormat()]
          }
        }
      }
    })

  public storeFormSMTPOffice365Configuration =
    new StoreForm<SMTPServerOffice365FormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [SMTPServerBasicFormFieldName.smtpRelayId]: {
            label: 'Relay',
            description: 'Relay to use to connect to the SMTP server',
            inputType: InputType.select,
            validators: this.storeRoot.environment.config.app.securerelay
              .customerside
              ? [mandatory()]
              : []
          },
          [SMTPServerOffice365FormFieldName.smtpOffice365TenantId]: {
            label: 'Tenant ID',
            description:
              'The tenant (directory) ID where your app is registered',
            inputType: InputType.input,
            validators: [mandatory()]
          },
          [SMTPServerOffice365FormFieldName.smtpOffice365ClientId]: {
            label: 'Client ID',
            description: 'The application (client) ID from Azure Entra',
            inputType: InputType.input,
            validators: [mandatory()]
          },
          [SMTPServerOffice365FormFieldName.smtpOffice365Secret]: {
            label: 'Client secret value',
            validators: [mandatorySecret(this, 'oauth2')],
            // Custom component used in the form
            inputType: undefined
          },
          [SMTPServerOffice365FormFieldName.smtpOffice365SenderName]: {
            label: 'Sender name',
            description:
              'Name that appears next to the sender’s email in the inbox',
            inputType: InputType.input,
            validators: [mandatory()]
          },
          [SMTPServerOffice365FormFieldName.smtpOffice365SenderEmail]: {
            label: 'Sender email address',
            inputType: InputType.input,
            validators: [mandatory()]
          }
        }
      }
    })

  public storeFormActivityLogsConfiguration =
    new StoreForm<ActivityLogsConfigurationFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [ActivityLogsConfigurationFormFieldName.isActive]: {
            label: 'Activate the Activity logs feature',
            inputType: InputType.switch,
            labelAlignItem: 'center'
          },
          [ActivityLogsConfigurationFormFieldName.retentionDurationInMonth]: {
            label: 'Retention duration (in month)',
            inputType: InputType.select,
            validators: [mandatory(), minValue(0)()],
            labelAlignItem: 'center'
          }
        }
      }
    })

  public storeFormSecurityConfiguration =
    new StoreForm<SecurityConfigurationFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [SecurityConfigurationFormFieldName.internalCertificate]: {
            label: 'Additional certificates',
            description:
              "Paste your company's PEM-encoded certificate chains here",
            inputType: InputType.textAreaCertificate
          }
        }
      }
    })

  public storeFormTenableAccountConfiguration =
    new StoreForm<TenableAccountFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [TenableAccountFormFieldName.defaultProfileId]: {
            label: 'Default profile id',
            validators: [mandatory()]
          },
          [TenableAccountFormFieldName.defaultRoleIds]: {
            label: 'Default role ids',
            validators: [mandatory()]
          }
        }
      }
    })

  public storeFormTelemetryConfiguration =
    new StoreForm<TelemetryFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [TelemetryFormFieldName.telemetryEnabled]: {
            label: 'Enable telemetry',
            inputType: InputType.switch
          }
        }
      }
    })

  public storeFormHealthCheckConfiguration =
    new StoreForm<HealthCheckFormFieldName>(this.storeRoot, {
      setup: {
        fields: {
          [HealthCheckFormFieldName.globalStatusDisplayEnabled]: {
            label: 'Show the Global Health Check Status',
            inputType: InputType.switch
          }
        }
      }
    })

  /* Observable */

  private $applicationSettings =
    observable.box<Maybe<EntityApplicationSettings>>(null)

  constructor(storeRoot: StoreRoot, options: IStoreOptions = {}) {
    super(storeRoot, options)
    makeObservable(this)
  }

  /**
   * Fetch application settings and save results into an entity.
   */
  fetchApplicationSettings() {
    this.storeFlagsAppSettingsFetch.loading()

    return Promise.resolve()
      .then(() => {
        return this.storeRoot
          .getGQLRequestor()
          .makeQuery<QueryApplicationSettings>(queryApplicationSettings)
      })
      .then(data => data.rbacApplicationSettings)
      .then(applicationSettings => {
        if (
          !checkRbac(
            this.storeRoot,
            this.storeFlagsAppSettingsFetch
          )(applicationSettings)
        ) {
          throw new ForbiddenAccessError()
        }

        const applicationSettingsEntity = createEntity<
          ApplicationSettings,
          EntityApplicationSettings
        >(EntityApplicationSettings, applicationSettings.node)

        this.setApplicationsSettings(applicationSettingsEntity)

        this.storeFlagsAppSettingsFetch.success()
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlagsAppSettingsFetch))
  }

  /**
   * Update application settings.
   */
  updateApplicationSettings(
    applicationSettings: InputUpdateApplicationSettings,
    options = {
      // if set to true, do not push a success message and forward the error exception
      forwardException: false
    }
  ) {
    this.storeFlagsAppSettingsSubmit.loading()

    return Promise.resolve()
      .then(() => {
        const args: MutationUpdateApplicationSettings['args'] = {
          applicationSettings
        }

        return this.storeRoot
          .getGQLRequestor()
          .makeQuery<MutationUpdateApplicationSettings>(
            mutationUpdateApplicationSettings,
            args
          )
      })
      .then(data => data.updateApplicationSettings)
      .then(updateApplicationSettings => {
        if (!options.forwardException) {
          this.storeRoot.stores.storeMessages.success(
            this.translate('Application settings updated'),
            {
              ariaRoles: ['alert'],
              labelledBy: 'applicationSettingsUpdated'
            }
          )
        }

        const applicationSettingsEntity = createEntity<
          ApplicationSettings,
          EntityApplicationSettings
        >(EntityApplicationSettings, updateApplicationSettings)

        this.setApplicationsSettings(applicationSettingsEntity)

        this.storeFlagsAppSettingsSubmit.success()
      })
      .catch(
        handleStoreError(this.storeRoot, this.storeFlagsAppSettingsSubmit, {
          forwardExceptionFn: () => options.forwardException
        })
      )
  }

  /**
   * Update application settings and Lockout Policy when being on the Tenable.ad
   * account configuration and "merge" sucesses/errors messages of both.
   */
  updateApplicationSettingsAndLockoutPolicy(
    applicationSettings: Maybe<InputUpdateApplicationSettings>,
    lockoutPolicy: Maybe<InputUpdateLockoutPolicy>
  ): Promise<any> {
    const { storeLockoutPolicy } = this.storeRoot.stores

    return Promise.all(
      filterFalsies([
        applicationSettings &&
          this.updateApplicationSettings(applicationSettings, {
            forwardException: true
          }),
        lockoutPolicy &&
          storeLockoutPolicy.editLockoutPolicy(lockoutPolicy, {
            forwardException: true
          })
      ])
    )
      .then(() => {
        this.storeRoot.stores.storeMessages.success(
          this.translate('Application settings updated'),
          {
            ariaRoles: ['alert'],
            labelledBy: 'applicationSettingsUpdated'
          }
        )
      })
      .catch(handleStoreError(this.storeRoot, this.storeFlagsAppSettingsSubmit))
  }

  /* Actions */

  /**
   * Save application settings.
   */
  @action
  setApplicationsSettings(
    applicationSettingsEntity: EntityApplicationSettings
  ): this {
    // Avoid saving secret/password replaced by smtpIsAccountPasswordDefined, smtpIsOffice365SecretDefined
    // to avoid filling the form with base64 values (which are not relevant for the end-user).
    const secretFields = ['smtpAccountPassword', 'smtpOffice365Secret'] as const

    secretFields.forEach(field => {
      delete applicationSettingsEntity[field]
    })

    this.$applicationSettings.set(applicationSettingsEntity)
    return this
  }

  /* Computed */

  @computed
  get applicationSettings(): Maybe<EntityApplicationSettings> {
    return this.$applicationSettings.get()
  }

  /**
   * Return true if the basic configuration is enabled.
   */
  @computed
  get isSmtpBasicConfigurationEnabled(): boolean {
    if (!this.applicationSettings) {
      return false
    }

    return (
      this.applicationSettings.smtpAuthenticationMethod ===
      SmtpAuthenticationMethod.Basic
    )
  }

  /**
   * Return true if the Office365 OAuth configuration is enabled.
   */
  @computed
  get isSmtpOffice365ConfigurationEnabled(): boolean {
    if (!this.applicationSettings) {
      return false
    }

    return (
      this.applicationSettings.smtpAuthenticationMethod ===
      SmtpAuthenticationMethod.Office365
    )
  }
}
